import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Link as LinkIcon,
  ContentCopy,
  Send,
  Info,
  People
} from '@mui/icons-material';
import reservationService from '../services/reservationService';

const GuestInfoStatus = ({ reservation, onUpdate }) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [guestInfoLink, setGuestInfoLink] = useState('');
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const isRequired = reservationService.isGuestInfoRequired(reservation);
  const isComplete = reservationService.isGuestInfoComplete(reservation);

  const getStatusInfo = () => {
    if (isComplete) {
      return {
        status: 'complete',
        color: 'success',
        icon: <CheckCircle />,
        text: 'Guest Information Provided',
        description: 'All guest information has been collected'
      };
    } else if (isRequired) {
      return {
        status: 'required',
        color: 'warning',
        icon: <Schedule />,
        text: 'Guest Information Required',
        description: 'Guest information needs to be collected before check-in'
      };
    } else {
      return {
        status: 'not_required',
        color: 'default',
        icon: <Info />,
        text: 'Guest Information Not Required',
        description: 'No additional guest information needed'
      };
    }
  };

  const statusInfo = getStatusInfo();

  const handleGenerateLink = () => {
    const link = reservationService.generateGuestInfoLink(reservation.confirmationCode, selectedLanguage);
    setGuestInfoLink(link);
    setShowLinkDialog(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(guestInfoLink);
      setShowCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleSendLink = () => {
    // This would integrate with an email service
    // For now, we'll just copy the link and show instructions
    handleCopyLink();
    alert('Link copied! You can now send it to your guests via email or messaging app.');
  };

  if (!isRequired && !isComplete) {
    return null; // Don't show component if not needed
  }

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <People sx={{ mr: 1 }} />
            <Typography variant="h6">
              Guest Information Status
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chip
              icon={statusInfo.icon}
              label={statusInfo.text}
              color={statusInfo.color}
              sx={{ mr: 2 }}
            />
            <Typography variant="body2" color="text.secondary">
              {statusInfo.description}
            </Typography>
          </Box>

          {isComplete && reservation.guestInformation && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Guest Information Summary:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {reservation.guestInformation.length} guest(s) information collected
              </Typography>
              {reservation.guestInfoCompletedAt && (
                <Typography variant="body2" color="text.secondary">
                  • Completed on: {new Date(reservation.guestInfoCompletedAt).toLocaleDateString()}
                </Typography>
              )}
              {reservation.updatedAt && !reservation.guestInfoCompletedAt && (
                <Typography variant="body2" color="text.secondary">
                  • Last updated: {new Date(reservation.updatedAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          )}

          {isRequired && !isComplete && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Guests need to provide their personal information, documents, and photos before check-in.
                Generate a secure link to send to your guests.
              </Alert>
              
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={handleGenerateLink}
              >
                Generate Guest Information Link
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Link Generation Dialog */}
      <Dialog
        open={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Guest Information Collection Link</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Language</InputLabel>
              <Select
                value={selectedLanguage}
                onChange={(e) => {
                  setSelectedLanguage(e.target.value);
                  const newLink = reservationService.generateGuestInfoLink(
                    reservation.confirmationCode, 
                    e.target.value
                  );
                  setGuestInfoLink(newLink);
                }}
              >
                <MenuItem value="en">🇺🇸 English</MenuItem>
                <MenuItem value="pt">🇧🇷 Português</MenuItem>
                <MenuItem value="es">🇪🇸 Español</MenuItem>
                <MenuItem value="fr">🇫🇷 Français</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Guest Information Link"
              value={guestInfoLink}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Tooltip title="Copy Link">
                    <IconButton onClick={handleCopyLink}>
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                )
              }}
              sx={{ mb: 2 }}
            />

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Instructions:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                • Send this link to your guests before their check-in date
                <br />
                • Each guest will need to provide their personal information
                <br />
                • The link can be used multiple times until all information is collected
                <br />
                • Guests will need to provide: name, email, phone, ID/passport, and photo
                <br />
                • All data is encrypted and stored securely
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLinkDialog(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            onClick={handleCopyLink}
          >
            Copy Link
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Send />}
            onClick={handleSendLink}
          >
            Copy & Send Instructions
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={showCopySuccess}
        autoHideDuration={3000}
        onClose={() => setShowCopySuccess(false)}
        message="Link copied to clipboard!"
      />
    </>
  );
};

export default GuestInfoStatus;
